import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { api } from '@/lib/api';

interface Result {
  type: string;
  id: string;
  label: string;
  sublabel?: string;
  href: string;
}

/**
 * Global search across machines, customers, orders, quotes and models.
 * The API only queries entity types the role may view, so results can never
 * hint at records the user has no access to.
 */
export function GlobalSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Result[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // ⌘K / Ctrl-K focuses search from anywhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Debounced so typing a serial number doesn't fire a query per keystroke.
  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    const timer = setTimeout(() => {
      void api
        .get<{ results: Result[] }>('/api/search', { q: query.trim() })
        .then((r) => {
          setResults(r.results);
          setHighlighted(0);
          setIsOpen(true);
        })
        .catch(() => setResults([]));
    }, 220);
    return () => clearTimeout(timer);
  }, [query]);

  const go = (r: Result) => {
    navigate(r.href);
    setQuery('');
    setIsOpen(false);
    void api.post('/api/recently-viewed', {
      entityType: r.type, entityId: r.id, label: r.label,
    }).catch(() => undefined);
  };

  return (
    <div ref={containerRef} className="relative w-full max-w-sm">
      <input
        ref={inputRef}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => results.length && setIsOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlighted((h) => Math.min(h + 1, results.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlighted((h) => Math.max(h - 1, 0));
          } else if (e.key === 'Enter' && results[highlighted]) {
            go(results[highlighted]);
          } else if (e.key === 'Escape') {
            setIsOpen(false);
          }
        }}
        placeholder="Search machines, customers, orders…  ⌘K"
        className="w-full rounded-full border border-rule bg-surface-hover px-4 py-1.5 text-body placeholder:text-ink-tertiary transition-all focus:border-secondary/60 focus:outline-none focus:ring-2 focus:ring-secondary/20 dark:bg-ink/[0.05]"
      />

      {isOpen && results.length > 0 && (
        <div className="card absolute left-0 right-0 top-full z-30 mt-2 max-h-80 overflow-y-auto !shadow-lg animate-slide-down">
          {results.map((r, i) => (
            <button
              key={`${r.type}-${r.id}`}
              onClick={() => go(r)}
              onMouseEnter={() => setHighlighted(i)}
              className={clsx(
                'flex w-full items-baseline justify-between gap-3 border-b border-divider px-4 py-2 text-left last:border-b-0',
                i === highlighted && 'bg-secondary/[0.06] dark:bg-secondary/15',
              )}
            >
              <span className="min-w-0">
                <span className="block truncate text-body text-ink">{r.label}</span>
                {r.sublabel && <span className="block truncate text-caption text-ink-tertiary">{r.sublabel}</span>}
              </span>
              <span className="label shrink-0">{r.type}</span>
            </button>
          ))}
        </div>
      )}

      {isOpen && query.trim().length >= 2 && results.length === 0 && (
        <div className="card absolute left-0 right-0 top-full z-30 mt-2 !shadow-lg px-4 py-3">
          <p className="font-sans text-caption text-ink-tertiary">No matches for “{query}”.</p>
        </div>
      )}
    </div>
  );
}
